import React from 'react';
import Icon from '../../../components/AppIcon';

const Footer = () => {
  const currentYear = new Date()?.getFullYear();

  const footerLinks = {
    company: [
      { label: 'About ProConnect', href: '#about' },
      { label: 'How It Works', href: '#benefits' },
      { label: 'Success Stories', href: '#testimonials' },
      { label: 'Contractor Resources', href: '#resources' }
    ],
    support: [
      { label: 'Contact Support', href: '#contact' },
      { label: 'FAQ', href: '#faq' },
      { label: 'Application Status', href: '#status' },
      { label: 'Contractor Portal', href: '#portal' }
    ],
    legal: [
      { label: 'Terms of Service', href: '#terms' },
      { label: 'Privacy Policy', href: '#privacy' },
      { label: 'Contractor Agreement', href: '#agreement' },
      { label: 'License Verification', href: '#verification' }
    ]
  };

  const serviceAreas = [
    'Tampa', 'St. Petersburg', 'Clearwater', 'Brandon',
    'Plant City', 'Wesley Chapel', 'Riverview', 'Lutz'
  ];

  const socialLinks = [
    { name: 'Facebook', icon: 'Facebook', href: '#facebook' },
    { name: 'LinkedIn', icon: 'Linkedin', href: '#linkedin' },
    { name: 'Twitter', icon: 'Twitter', href: '#twitter' },
    { name: 'YouTube', icon: 'Youtube', href: '#youtube' }
  ];

  return (
    <footer className="bg-foreground text-background">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            {/* Logo */}
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center mr-3">
                <Icon name="Zap" size={24} color="white" strokeWidth={2.5} />
              </div>
              <div className="text-2xl font-bold">
                Pro<span className="text-primary">Connect</span>
              </div>
            </div>

            <p className="text-background/80 mb-6 leading-relaxed">
              Tampa Bay's exclusive premium home service marketplace connecting 
              licensed contractors with high-value customers.
            </p>

            {/* Contact Info */}
            <div className="space-y-3">
              <div className="flex items-center">
                <Icon name="Phone" size={16} className="mr-3 text-primary" />
                <span className="text-background/90">(813) 555-0199</span>
              </div>
              <div className="flex items-center">
                <Icon name="Mail" size={16} className="mr-3 text-primary" />
                <span className="text-background/90">contractors@proconnect.com</span>
              </div>
              <div className="flex items-center">
                <Icon name="MapPin" size={16} className="mr-3 text-primary" />
                <span className="text-background/90">Tampa Bay, Florida</span>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex space-x-4 mt-6">
              {socialLinks?.map((social) => (
                <a
                  key={social?.name}
                  href={social?.href}
                  className="w-10 h-10 bg-background/10 hover:bg-primary rounded-lg flex items-center justify-center transition-colors duration-200"
                  aria-label={social?.name}
                >
                  <Icon name={social?.icon} size={20} className="text-background" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="grid sm:grid-cols-3 gap-8 lg:col-span-3">
            {/* Company Links */}
            <div>
              <h3 className="text-lg font-semibold text-background mb-4">Company</h3>
              <ul className="space-y-3">
                {footerLinks?.company?.map((link) => (
                  <li key={link?.label}>
                    <a
                      href={link?.href}
                      className="text-background/80 hover:text-primary transition-colors duration-200"
                    >
                      {link?.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Support Links */}
            <div>
              <h3 className="text-lg font-semibold text-background mb-4">Support</h3>
              <ul className="space-y-3">
                {footerLinks?.support?.map((link) => (
                  <li key={link?.label}>
                    <a
                      href={link?.href}
                      className="text-background/80 hover:text-primary transition-colors duration-200"
                    >
                      {link?.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal Links */}
            <div>
              <h3 className="text-lg font-semibold text-background mb-4">Legal</h3>
              <ul className="space-y-3">
                {footerLinks?.legal?.map((link) => (
                  <li key={link?.label}>
                    <a
                      href={link?.href}
                      className="text-background/80 hover:text-primary transition-colors duration-200"
                    >
                      {link?.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Service Areas */}
        <div className="mt-12 pt-8 border-t border-background/20">
          <h3 className="text-lg font-semibold text-background mb-4">Service Areas</h3>
          <div className="flex flex-wrap gap-3">
            {serviceAreas?.map((area) => (
              <span
                key={area}
                className="px-3 py-1 bg-background/10 text-background/90 rounded-full text-sm"
              >
                {area}
              </span>
            ))}
          </div>
        </div>

        {/* Credentials & Certifications */}
        <div className="mt-12 pt-8 border-t border-background/20">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="flex items-center">
              <Icon name="Shield" size={20} className="text-accent mr-3" />
              <div>
                <div className="font-semibold text-background">Licensed & Insured</div>
                <div className="text-sm text-background/70">Florida State Verified</div>
              </div>
            </div>
            
            <div className="flex items-center">
              <Icon name="Award" size={20} className="text-warning mr-3" />
              <div>
                <div className="font-semibold text-background">BBB Accredited</div>
                <div className="text-sm text-background/70">A+ Rating</div>
              </div>
            </div>
            
            <div className="flex items-center">
              <Icon name="CreditCard" size={20} className="text-primary mr-3" />
              <div>
                <div className="font-semibold text-background">Payment Protected</div>
                <div className="text-sm text-background/70">100% Guarantee</div>
              </div>
            </div>
            
            <div className="flex items-center">
              <Icon name="Users" size={20} className="text-secondary mr-3" />
              <div>
                <div className="font-semibold text-background">265+ Contractors</div>
                <div className="text-sm text-background/70">Trusted Network</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Bottom Bar */}
      <div className="border-t border-background/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-center">
            <div className="text-background/70 text-sm mb-4 sm:mb-0">
              © {currentYear} ProConnect. All rights reserved. | Florida License #ABC123456
            </div>
            
            <div className="flex items-center space-x-6 text-sm">
              <span className="text-background/70">
                Made for Tampa Bay contractors
              </span>
              <div className="flex items-center text-accent">
                <Icon name="MapPin" size={14} className="mr-1" />
                <span>Tampa Bay, FL</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Emergency Contact Banner */}
      <div className="bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-center text-center">
            <Icon name="Phone" size={16} className="mr-2 text-primary-foreground" />
            <span className="text-primary-foreground font-medium">
              Contractor Emergency Support: (813) 555-0199 (24/7)
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;